<template>
  <client-only>
    <div
      class="relative mt-1"
      :id="`dropdown-container-${name}`"
      v-click-away="closeMenu"
      :class="parentContainer"
    >
      <div
        :tabindex="tabindex"
        @keydown.tab="openMenu"
        @keydown.enter="openMenu"
        class="flex items-center text-white cursor-pointer relative w-full px-4 py-3 rounded-md font-normal text-lg border h-[50px] leading-6 font-ff-good-pro-cond shadow-sm focus:ring-0 focus-visible:outline-none"
        :class="[
          hasError ? '!border-red-500 font-medium italic' : '',
          containerClass,
          isVisibleMenu ? 'border-primary' : '',
        ]"
        @click="toggleMenu"
      >
        <span v-if="!selectedValue" :class="placeholderClass">
          {{ placeholder ? placeholder : t("common.placeholder") }}
        </span>
        <span v-else-if="hasSlot('display-name')" :class="inputTextClass">
          <slot name="display-name" :selected="selectedValue" />
        </span>
        <span v-else :class="inputTextClass">
          {{ selectedValue[label] }}
        </span>

        <span class="absolute flex right-1">
          <ClearIcon
            class="w-5 h-5 text-opacity-50"
            :class="inputTextClass"
            v-if="modelValue"
            @click.stop="modelValue = null"
          />
          <AngleUp
            class="w-5 h-5 text-opacity-50"
            :class="inputTextClass"
            v-if="isVisibleMenu"
            @click.stop="toggleMenu"
          />
          <AngleDown
            class="w-5 h-5 text-opacity-50"
            :class="inputTextClass"
            @click.stop="toggleMenu"
            v-else
          />
        </span>
      </div>
      <transition-group
        name="selector-dropdown"
        tag="div"
        appear
        class="relative"
      >
        <div
          v-if="isVisibleMenu"
          @click="closeMenu"
          class="md:hidden block fixed md:relative inset-0 cursor-pointer top-0 w-full h-full z-[9999] bg-black bg-opacity-60"
          key="key-backdrop"
        ></div>
        <span
          v-if="isVisibleMenu"
          @click="closeMenu"
          class="list-container-span"
        ></span>
        <div
          v-if="isVisibleMenu"
          class="sl-dropdown-menu"
          :class="
            hasTop ? 'sl-dropdown-menu-has-top' : 'sl-dropdown-menu-has-bottom'
          "
          key="key-header"
        >
          <h3
            class="text-[22px] leading-[32px] text-black block md:hidden px-4"
          >
            {{ title }}
          </h3>
          <CloseIcon
            class="block z-10 w-[30px] cursor-pointer h-[30px] md:hidden text-[#787878] absolute top-2 right-2"
            @click="closeMenu"
          />
          <div class="relative h-[50px] flex items-center" v-if="searchField">
            <SearchIcon class="w-5 h-5 text-[#99A2AF] absolute left-2" />
            <input
              type="text"
              :tabindex="tabindex"
              @keydown.tab="toggleMenu"
              ref="searchInputBox"
              :placeholder="$t('common.search')"
              @input="onSearchInput"
              class="w-full text-[#8D8D8D] h-[50px] focus:ring-0 pl-9 pr-4 border-b border-t-0 border-x-0 border-[#eaedf3] focus:border-gray-500 placeholder:!text-[#8D8D8D] search-input"
            />
          </div>
          <div
            class="px-5 min-h-10 space-x-[22px] border-after flex overflow-x-auto no-scrollbar w-full items-center gap-2.5 text-[13px] italic font-bold uppercase font-ff-good-pro-cond my-0"
          >
            <span
              v-for="(type, key) in selectedOptions"
              :key="key"
              @click="() => loadProducts(type)"
              class="inline-flex items-center justify-center h-10 py-1 border-b cursor-pointer whitespace-nowrap"
              :class="
                selectedTypes == type.original_name
                  ? 'border-primary  text-primary'
                  : 'border-transparent text-dark-gray'
              "
            >
              {{ type.name }}
            </span>
          </div>

          <div class="h-full px-4 overflow-y-auto list-container no-scrollbar">
            <template
              v-for="(products, menuTitle, realIndex) in productModels"
              :key="`key-${realIndex}`"
              class="h-full px-4 list-container"
            >
              <h3
                class="relative mt-5 mb-0 text-sm italic font-bold uppercase text-dark-gray-600 font-ff-good-pro-cond"
              >
                {{ menuTitle }}
              </h3>
              <transition-group
                name="list-item"
                tag="ul"
                appear
                class="my-4 divide-y md:my-0 font-ff-good-pro-cond divide-light-gray-300"
              >
                <li
                  v-for="(model, prdIndex) in products"
                  :key="`key-${index}`"
                  @click="() => onSelect(model)"
                  class="h-[54px] relative flex items-center cursor-pointer hover:text-primary"
                  :class="[
                    modelValue == model[uniqueKey]
                      ? 'selector__selected-options'
                      : '',
                  ]"
                >
                  <slot name="option" :option="model" />
                </li>
              </transition-group>
            </template>
          </div>
        </div>
      </transition-group>
      <p
        v-if="hasError && hasError"
        class="mt-1 text-sm italic font-medium text-red-500"
        v-text="errorMessage"
      />
    </div>
  </client-only>
</template>
<script setup>
import { useTranslation } from "i18next-vue";
import AngleUp from "@/assets/svg/heroicons/angle-up.svg?component";
import AngleDown from "@/assets/svg/heroicons/angle-down.svg?component";
import SearchIcon from "@/assets/svg/heroicons/search-2-line.svg?component";
import CloseIcon from "@/assets/svg/heroicons/x-circle.svg?component";
import ClearIcon from "@/assets/svg/heroicons/close.svg?component";
import debounce from "lodash-es/debounce";

const { t } = useTranslation();

const props = defineProps({
  options: {
    type: Array,
    default: [],
  },
  searchField: {
    type: Boolean,
    default: true,
  },
  parentContainer: {
    type: String,
    default: "",
  },
  uniqueKey: {
    type: String,
    default: "id",
  },
  name: {
    type: String,
    default: "name",
  },
  tabindex: {
    type: String,
    default: "0",
  },
  containerClass: {
    type: String,
    default:
      "focus:border-black bg-[#FFFFFF] border-[#DBDBDB] bg-opacity-[0.06]",
  },
  modelValue: {
    type: [Array, Object, String],
    default: [],
  },
  valueField: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: null,
  },
  placeholderClass: {
    type: String,
    default: "text-black text-opacity-70",
  },
  title: {
    type: String,
    default: null,
  },
  inputTextClass: {
    type: String,
    default: "text-black",
  },
  errorMessage: {
    type: String,
    default: null,
  },
  hasError: {
    type: Boolean,
    default: false,
  },
  clearValidation: {
    type: Function,
    default: null,
  },
  label: {
    type: String,
    default: "name",
  },
  url: {
    type: String,
    default: null,
  },
});

const slots = useSlots();
const emit = defineEmits();
const searchInputBox = ref(null);

const isVisibleMenu = ref(false);

let productsData = null;
if (props.modelValue) {
  props.options.filter((sl) => {
    for (const key in sl.products) {
      if (
        Object.prototype.hasOwnProperty.call(sl.products, key) &&
        !productsData
      ) {
        productsData = sl.products[key].find(
          (_p) => props.modelValue == _p[props.uniqueKey]
        );
      }
    }
  });
}

const selectedValue = ref(productsData);
const lastSearch = ref(null);
const hasTop = ref(false);
let selectedOptions = ref(props.options);
let productModels = ref([]);

const selectedTypes = ref(null);

if (selectedOptions.value.length) {
  selectedTypes.value = selectedOptions.value[0].original_name;
  loadProducts(selectedOptions.value[0]);
}

watch(
  props,
  (newValue, oldValue) => {
    if (!newValue.modelValue || newValue.modelValue == "undefined") {
      selectedValue.value = null;
    }
  },
  { deep: true }
);

function loadProducts(type) {
  productModels.value = type.products;
  selectedTypes.value = type.original_name;
}

const toggleMenu = () => {
  isVisibleMenu.value = !isVisibleMenu.value;
  if (isVisibleMenu.value) {
    setTimeout(() => {
      // searchInputBox.value.focus();
    }, 200);
  }
  setTimeout(() => {
    hasTop.value = dropdownExceedsBottomViewport();
  }, 200);
};
const openMenu = () => {
  isVisibleMenu.value = true;
  setTimeout(() => {
    hasTop.value = dropdownExceedsBottomViewport();
  }, 200);
};
const closeMenu = () => {
  isVisibleMenu.value = false;
};

function dropdownExceedsBottomViewport() {
  var dContainer = document.getElementById(`dropdown-container-${props.name}`);
  var dropdown_top = dContainer.getBoundingClientRect().top,
    dropdown_height = dContainer.scrollHeight,
    viewport_height = document.documentElement.clientHeight;

  return dropdown_top + dropdown_height > viewport_height;
}

const onSelect = (slValue) => {
  selectedValue.value = slValue;
  isVisibleMenu.value = false;

  if (props.valueField) {
    emit("update:modelValue", selectedValue.value[props.valueField]);
  } else {
    emit("update:modelValue", selectedValue.value);
  }
  emit("input");
};

const onSearchInput = (slValue) => {
  if (props.url) {
    searchInput(slValue.target.value);
  } else {
    if (!slValue.target.value) {
      selectedOptions.value = props.options;
      if (selectedOptions.value.length) {
        selectedTypes.value = selectedOptions.value[0].original_name;
        loadProducts(selectedOptions.value[0]);
      }
    } else {
      selectedOptions.value = props.options.filter((sl) => {
        let productsData = null;
        for (const key in sl.products) {
          if (Object.prototype.hasOwnProperty.call(sl.products, key)) {
            const slPr = sl.products[key].filter((_p) =>
              _p.name.toLowerCase().includes(slValue.target.value.toLowerCase())
            );
            if (slPr.length) {
              productsData = {
                ...productsData,
                [key]: slPr,
              };
            }
          }
        }
        if (productsData) {
          return {
            ...sl,
            products: productsData,
          };
        } else {
          return false;
        }
      });

      if (selectedOptions.value.length) {
        selectedTypes.value = selectedOptions.value[0].original_name;
        loadProducts(selectedOptions.value[0]);
      } else {
        productModels.value = [];
      }
    }
  }
};

const searchInput = (search) => {
  search = search || selectedValue.value.search;

  if (lastSearch.value === search) {
    return false;
  }
  lastSearch.value = search;

  loading(true);
  request(search, loading);

  const request = debounce(async (search, loading) => {
    const { data } = await useAPI(url.value, {
      params: {
        query: search,
      },
    });
    loading(false);
  }, 3500);
};

function hasSlot(name = "default") {
  if (slots) {
    return !!slots[name];
  } else {
    return false;
  }
}
</script>
<style scoped>
.sl-dropdown-menu.sl-dropdown-menu-has-top {
  @apply md:bottom-[55px] md:top-auto;
}
.sl-dropdown-menu.sl-dropdown-menu-has-bottom {
  @apply md:bottom-auto md:top-1;
}
.sl-dropdown-menu {
  --vs-dropdown-max-height: 378px;
  box-shadow: 0px 0px 18px 0px rgba(36, 38, 46, 0.12);
  @apply bottom-0  md:absolute max-h-[442px] md:flex flex-col  h-auto fixed top-auto  w-full max-w-full py-2 md:py-0 border-none bg-white z-[99999] rounded-t-md md:rounded-md md:overflow-hidden left-0;
}
.search-input::placeholder {
  @apply !text-[#8D8D8D];
}

.selector__selected-options:after {
  content: "";
  background-image: url("/double-correct.svg");
  @apply w-5 h-5 absolute right-2;
}
.selector__selected-options {
  @apply font-semibold;
}

.list-container {
  height: calc(100% - 135px);
}

.list-container-span {
  @apply bg-[#C2C2C2] top-[120px] w-8 h-1 right-1/2 md:hidden block fixed md:relative rounded-md;
}

.border-after {
  @apply relative;
}
.border-after::after {
  content: "";
  @apply h-[1px] w-full absolute bottom-0 bg-[#EAEDF3] left-0 z-[-1];
}

.selector-dropdown-leave-active {
  animation-name: selector-dropdown-leave-desktop-animation;
  animation-duration: 0.15s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;
  opacity: 0;
  transform: translateY(66%);
}
.selector-dropdown-enter-active {
  animation-name: selector-dropdown-desktop-animation;
  animation-duration: 0.15s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;
  opacity: 0;
  transform: translateY(66%);
}

@keyframes list-item-animation {
  from {
    opacity: 0;
    transform: translateY(66%);
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.list-item-enter-active,
.list-item-leave-active {
  animation-name: list-item-desktop-animation;
  animation-delay: calc(0.06s * var(--i));
  animation-duration: 0.1s;
  animation-fill-mode: forwards;
  animation-timing-function: ease;
  opacity: 0;
  transform: translateY(-26%);
}

@keyframes selector-dropdown-animation {
  from {
    opacity: 0;
    transform: translateY(66%);
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
@keyframes selector-dropdown-leave-animation {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    transform: translateY(66%);
    opacity: 0;
  }
}

@keyframes selector-dropdown-desktop-animation {
  from {
    opacity: 0;
    transform: translateY(-26%);
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}
@keyframes selector-dropdown-leave-desktop-animation {
  from {
    opacity: 1;
    transform: translateY(0);
  }

  to {
    transform: translateY(-26%);
    opacity: 0;
  }
}

@media (max-width: 767px) {
  .selector-dropdown-leave-active {
    animation-name: selector-dropdown-leave-animation;
    transform: translateY(66%);
  }
  .selector-dropdown-enter-active {
    animation-name: selector-dropdown-animation;
    transform: translateY(66%);
  }
  .sl-dropdown-menu {
    --vs-dropdown-max-height: calc(100dvh - 150px);
    max-height: calc(100dvh - 150px);
    @apply shadow-none;
  }
  .list-container {
    height: calc(100dvh - 275px);
  }
}
</style>